// v3.18-1387 CRM-001 — Rep picker dialog used by:
//   • Row-level "Transfer Rep" button on the Clients list Actions column.
//   • Bulk "Transfer Rep" button on the Clients list bulk action bar.
//
// Loads the eligible-rep roster once per open from
// GET /api/clients/rep-picker and posts to the chosen endpoint on
// confirm.
import { useEffect, useMemo, useState } from 'react';
import { UserCog, Loader2, Search, X } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * Props:
 *   open, onClose
 *   mode: 'single' | 'bulk'
 *   client        (single mode) — the client row being transferred
 *   clientIds     (bulk mode)   — array of client IDs
 *   onTransferred(result)       — { sales_rep_id, sales_rep_name, ok, failed }
 */
export default function RepPickerDialog({
  open, onClose, mode = 'single', client = null, clientIds = [],
  onTransferred,
}) {
  const [reps, setReps]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery]     = useState('');
  const [pickedId, setPicked] = useState('');
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked(''); setQuery('');
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/rep-transfer/reps');
        setReps(data?.reps || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load reps');
      } finally { setLoading(false); }
    })();
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return reps;
    return reps.filter((r) =>
      (r.full_name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.role || '').toLowerCase().includes(q));
  }, [reps, query]);

  const currentRepId = client?.sales_rep_id || null;

  const confirm = async () => {
    if (!pickedId) return;
    setSaving(true);
    try {
      if (mode === 'single' && client?.id) {
        const { data } = await api.post(
          `/rep-transfer/single/${client.id}`, { new_rep_id: pickedId });
        toast.success(`Transferred to ${data.sales_rep_name}`);
        onTransferred?.({ mode: 'single', ...data });
      } else if (mode === 'bulk' && clientIds.length) {
        const { data } = await api.post('/rep-transfer/bulk',
          { client_ids: clientIds, new_rep_id: pickedId });
        toast.success(
          `Transferred ${data.ok_count} client${data.ok_count === 1 ? '' : 's'}`
          + (data.failed_count ? ` · ${data.failed_count} failed` : '')
          + ` → ${data.new_rep?.full_name}`);
        onTransferred?.({ mode: 'bulk', ...data });
      }
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transfer failed');
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3"
      data-testid="rep-picker-dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-900 text-white">
          <div className="text-sm font-bold flex items-center gap-2">
            <UserCog className="w-4 h-4" />
            {mode === 'bulk'
              ? `Transfer ${clientIds.length} client${clientIds.length === 1 ? '' : 's'}`
              : `Transfer ${client?.full_name || 'client'}`}
          </div>
          <button onClick={onClose} data-testid="rep-picker-close"
                  className="text-slate-300 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="rep-picker-search"
              placeholder="Search reps by name, email, or role…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 text-sm pl-8"
            />
          </div>
          {mode === 'single' && client?.sales_rep_name && (
            <div className="text-[11px] text-slate-500 mt-2">
              Currently assigned to <b>{client.sales_rep_name}</b>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto divide-y divide-slate-100" data-testid="rep-picker-list">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading reps…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              No eligible reps found.
            </div>
          ) : filtered.map((r) => {
            const active = pickedId === r.id;
            const isCurrent = r.id === currentRepId;
            return (
              <button
                key={r.id}
                onClick={() => setPicked(r.id)}
                disabled={isCurrent}
                data-testid={`rep-option-${r.id}`}
                className={
                  'block w-full text-left px-4 py-2.5 transition ' +
                  (active ? 'bg-slate-900 text-white'
                    : isCurrent ? 'bg-slate-50 text-slate-400 cursor-not-allowed'
                                : 'hover:bg-slate-50 text-slate-800')
                }
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {r.full_name}
                      {isCurrent && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide">(current)</span>
                      )}
                    </div>
                    <div className={'text-[11px] truncate ' +
                        (active ? 'text-slate-300' : 'text-slate-500')}>
                      {r.email}
                    </div>
                  </div>
                  <span className={
                    'text-[10px] px-1.5 py-0.5 rounded-full uppercase font-bold ' +
                    (active ? 'bg-white/25' : 'bg-slate-100 text-slate-600')
                  }>{r.role}</span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50">
          <Button variant="outline" size="sm" onClick={onClose} className="text-xs">
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!pickedId || saving}
            onClick={confirm}
            data-testid="rep-picker-confirm"
            className="bg-slate-900 hover:bg-black text-white text-xs"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
            Transfer
          </Button>
        </div>
      </div>
    </div>
  );
}
